import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { WalletServices } from "../../../services/wallet/WalletService";
import { AdminServices } from "../../../services/admin/AdminServices";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EditIcon from "@mui/icons-material/Edit";
import { auth } from "../../../config/firebase";
import toast, { Toaster } from "react-hot-toast";

interface Wallet {
  balance: string;
  id: string;
  txnAmount: string;
  type: string;
  date: string;
  metadata: {
    txnRef: any;
    campaignId: string;
  };
  initiatedBy: string;
}

interface walletInfo {
  balance: string;
  totalPages: number;
  totalElements: number;
  transactions: Array<Wallet>;
}

/*balance
    :
    "400.00"
date
    :
    "2024-10-07T17:38:24"
id
    :
    "1728322703843"
initiatedBy
    :
    "7om3TC6Of1eW5B8u2HUJ35dfhZm1"
metadata
    :
    campaignId
        :
        "1728322678581"
txnRef
    :
    null
        [[Prototype]]
:
Object
txnAmount
    :
    "40.0"
type
    :
    "HOLD"*/

const WalletComponent: React.FC = () => {
  const [walletInfo, setWalletInfo] = useState<walletInfo | null>(null);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState<boolean>(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState<boolean>(false);
  const [selectedWallet, setSelectedWallet] = useState<walletInfo | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [action, setAction] = useState<"credit" | "debit">("credit");
  const [orgIdInput, setOrgIdInput] = useState<string>("");
  const [orgId, setOrgId] = useState<string>("");
  const currentUser = auth.currentUser;

  const fetchWalletHistory = async (orgId: string) => {
    setLoading(true);
    try {
      const response = await WalletServices.getWalletHistory(orgId, 0);
      setWalletInfo(response.response);
    } catch (error) {
      console.error("Error fetching wallet history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetBalance = async (orgId: string) => {
    try {
      const response = await WalletServices.getWalletBalance(orgId);
      setBalance(response.balance); // assuming response contains balance
      setBalanceDialogOpen(true);
    } catch (error) {
      console.error("Error fetching wallet balance:", error);
    }
  };

  const handleUpdateWallet = async () => {
    try {
      let resolvedOrgId = orgId;

      // Step 1: Resolve orgId if email
      if (isEmail(orgId)) {
        const { response } = await AdminServices.getUserByEmail(orgId);

        if (!response?.organization?.orgId) {
          throw new Error("Organization not found");
        }

        resolvedOrgId = response.organization.orgId;
      }

      if (!resolvedOrgId) {
        throw new Error("Invalid organization ID");
      }

      // Step 2: Call wallet API
      const payload = {
        orgId: resolvedOrgId,
        amount,
        userId: "",
      };

      if (action === "credit") {
        await WalletServices.creditWallet(payload);
      } else {
        await WalletServices.debitWallet(payload);
      }

      // Step 3: Refresh UI
      await fetchWalletHistory(resolvedOrgId);

      setOrgId(resolvedOrgId);
      setUpdateDialogOpen(false);

      toast.success(`Wallet ${action}ed successfully`);
    } catch (error: any) {
      if (error?.response?.data.errorCode === "USER_ERR-005") {
        toast.error("Enter a registered email or valid org ID");
      } else if (error?.response?.data.errorCode === "ERR-000") {
        toast.error("Amount cannot be empty");
      } else {
        toast.error(`Failed to ${action} wallet`);
      }

      console.error(`Error ${action}ing wallet:`, error);
    }
  };

  const handleOpenUpdateDialog = () => {
    // setSelectedWallet(wallet);
    setAmount("");
    setAction("credit");
    // setOrgId(orgId);
    setOrgId(orgIdInput ? orgIdInput : "");
    setUpdateDialogOpen(true);
  };

  const handleFetchWalletHistory = async () => {
    try {
      if (isEmail(orgIdInput)) {
        const { response } = await AdminServices.getUserByEmail(orgIdInput);

        if (!response?.organization?.orgId) {
          throw new Error("Organization not found");
        }

        setOrgId(response.organization.orgId);
        fetchWalletHistory(response.organization.orgId);
      } else {
        fetchWalletHistory(orgIdInput);
        setOrgId(orgIdInput);
      }
    } catch (error) {
      if((error as any).response.data.errorCode === "USER_ERR-005")
          toast.error("User does not exist ")
      else {
        toast.error("Failed to fetch wallet history")
      }
      
      console.error("Failed to fetch wallet history:", error);
    }
  };

  const isEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div>
        <TextField
          label="Org ID or Email"
          value={orgIdInput}
          onChange={(e) => setOrgIdInput(e.target.value)}
          fullWidth
          margin="normal"
        />
        <div>
          <Button onClick={handleFetchWalletHistory} disabled={!orgIdInput}>
            Fetch Wallet History
          </Button>
          <button
            type="submit"
            className="px-3 py-3 font-semibold text-white transition rounded bg-violet-600 hover:bg-violet-700"
            onClick={() => handleOpenUpdateDialog()}
          >
            Update Wallet
          </button>
          {/* <Button onClick={() => handleOpenUpdateDialog()}>Update Wallet</Button> */}
        </div>

        {walletInfo && <h2>{`Total Balance: ${walletInfo?.balance}`}</h2>}

        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2}>
            {walletInfo?.transactions?.map((transaction) => (
              <Grid item xs={12} sm={6} md={4} key={transaction.id}>
                <Paper elevation={3} style={{ padding: 16 }}>
                  <div>Transaction ID: {transaction.id}</div>
                  <div>Balance: {transaction.balance}</div>
                  <div>Transaction amount: {transaction?.txnAmount}</div>
                  <div>Transaction type: {transaction?.type}</div>
                  <IconButton onClick={() => handleGetBalance(orgId)}>
                    <AccountBalanceWalletIcon />
                  </IconButton>
                  {/* {transaction.id === walletInfo?.transactions[0]?.id && (
                  <IconButton
                    onClick={() => handleOpenUpdateDialog(walletInfo)}
                  >
                    <EditIcon />
                  </IconButton>
                )} */}
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog
          open={balanceDialogOpen}
          onClose={() => setBalanceDialogOpen(false)}
        >
          <DialogTitle>Wallet Balance</DialogTitle>
          <DialogContent>
            <div>Balance: {balance}</div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBalanceDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={updateDialogOpen}
          onClose={() => setUpdateDialogOpen(false)}
        >
          <DialogTitle>Update Wallet</DialogTitle>
          <DialogContent>
            <TextField
              label="Org ID or Email"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              select
              label="Action"
              value={action}
              onChange={(e) => setAction(e.target.value as "credit" | "debit")}
              fullWidth
              margin="normal"
            >
              <MenuItem value="credit">Credit</MenuItem>
              <MenuItem value="debit">Debit</MenuItem>
            </TextField>
            <TextField
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUpdateWallet}>OK</Button>
            <Button onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default WalletComponent;
